/**
 * @copyright
 * Copyright (c) 2022 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import cloneDeep from "lodash/fp/cloneDeep";

import { RequestStatus } from "~/constants";
import { getReferrerSitesAsync, postDecodeAsync, postEncodeAsync } from "~/features/oneClick/thunks";
import { DecodedDataType, OneClickState } from "~/features/oneClick/types";

const initialState: OneClickState = {
  encode: {
    meta: {
      requestStatus: RequestStatus.IDLE,
    },
    data: "",
  },
  decode: {
    meta: {
      requestStatus: RequestStatus.IDLE,
    },
    data: {
      AFNO: "",
      email: "",
      pwd: "",
      rid: "",
    },
  },
  sites: {
    meta: {
      requestStatus: RequestStatus.IDLE,
    },
    data: {},
  },
};

const oneClickSlice = createSlice({
  name: "oneClick",
  initialState,
  reducers: {
    clearState: (state) => {
      state.encode = cloneDeep(initialState.encode);
      state.decode = cloneDeep(initialState.decode);
      state.sites = cloneDeep(initialState.sites);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getReferrerSitesAsync.pending, (state) => {
        const { sites } = state;

        sites.meta.requestStatus = RequestStatus.PENDING;
      })
      .addCase(getReferrerSitesAsync.fulfilled, (state, action: PayloadAction<{ [key: number]: string }>) => {
        const { sites } = state;

        sites.meta.requestStatus = RequestStatus.SUCCESS;
        sites.data = action.payload;
      })

      .addCase(getReferrerSitesAsync.rejected, (state) => {
        const { sites } = state;

        sites.meta.requestStatus = RequestStatus.ERROR;
      })
      .addCase(postEncodeAsync.pending, (state) => {
        const { encode } = state;

        encode.meta.requestStatus = RequestStatus.PENDING;
      })

      .addCase(postEncodeAsync.fulfilled, (state, action: PayloadAction<string>) => {
        const { encode } = state;

        encode.meta.requestStatus = RequestStatus.SUCCESS;
        encode.data = action.payload;
      })

      .addCase(postEncodeAsync.rejected, (state) => {
        const { encode } = state;

        encode.meta.requestStatus = RequestStatus.ERROR;
      })
      .addCase(postDecodeAsync.pending, (state) => {
        const { decode } = state;

        decode.meta.requestStatus = RequestStatus.PENDING;
      })

      .addCase(postDecodeAsync.fulfilled, (state, action: PayloadAction<DecodedDataType>) => {
        const { decode } = state;

        decode.meta.requestStatus = RequestStatus.SUCCESS;
        decode.data = action.payload;
      })

      .addCase(postDecodeAsync.rejected, (state) => {
        const { decode } = state;

        decode.meta.requestStatus = RequestStatus.ERROR;
      });
  },
});

export const OneClickActions = oneClickSlice.actions;

export const oneClickReducer = oneClickSlice.reducer;
